import { useMemo, useState, useEffect } from 'react'
import { Typography, Button } from '@material-tailwind/react'
import { FiArrowLeft } from 'react-icons/fi'
import { visitServices } from '@/services/visit-services'
import { UserServices } from '@/services/accesscontrol-services'
import { fmtDate, fmtLocalDT } from './utils'
import { InviteForm } from './invite-form'
import { InviteSuccess } from './invite-success'

export const VisitInviteForm = () => {
  const now = useMemo(() => { const d = new Date(); d.setMinutes(0,0,0); return d }, [])
  const in2h = useMemo(() => new Date(now.getTime() + 2 * 60 * 60 * 1000), [now])

  const [mode, setMode] = useState('full_day')
  const [date, setDate] = useState(fmtDate(now))
  const [startAt, setStartAt] = useState(fmtLocalDT(now))
  const [endAt, setEndAt] = useState(fmtLocalDT(in2h))
  const [officeId, setOfficeId] = useState(null)
  const [offices, setOffices] = useState([])
  const [officesLoading, setOfficesLoading] = useState(true)
  const [invitees, setInvitees] = useState([{ name: '', email: '' }])
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [sentInvites, setSentInvites] = useState([])
  const [qrOpen, setQrOpen] = useState(false)
  const [qrTarget, setQrTarget] = useState(null)

  const userServices = new UserServices()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrorMsg('')
    if (!officeId) return setErrorMsg('Debes seleccionar una dependencia.')

    const validInvitees = invitees
      .map(i => ({ name: i.name.trim(), email: i.email.trim().toLowerCase() }))
      .filter(i => i.name && i.email)
    if (validInvitees.length === 0) return setErrorMsg('Agrega al menos un invitado con nombre y correo.')

    const body = mode === 'full_day'
      ? { kind: 'full_day', date, office_id: String(officeId), invitees: validInvitees }
      : { kind: 'range', start_at: startAt, end_at: endAt, office_id: String(officeId), invitees: validInvitees }

    if (body.kind === 'range' && new Date(body.end_at) <= new Date(body.start_at))
      return setErrorMsg('El término debe ser posterior al inicio.')

    try {
      setIsLoading(true)
      const result = await visitServices.sendInvitations({ body })
      setSentInvites(Array.isArray(result?.invitations) ? result.invitations : [])
      setIsSuccess(true)
    } catch (err) {
      setErrorMsg(err?.response?.data?.error || err.message || 'Error al enviar invitaciones')
    } finally {
      setIsLoading(false)
    }
  }

  const openQr = (invite) => { setQrTarget(invite); setQrOpen(true) }
  const closeQr = () => { setQrOpen(false); setQrTarget(null) }

  const backToForm = () => {
    setIsSuccess(false)
    setSentInvites([])
    setInvitees([{ name: '', email: '' }])
    setMode('full_day')
    setDate(fmtDate(now))
    setStartAt(fmtLocalDT(now))
    setEndAt(fmtLocalDT(in2h))
    setOfficeId(null)
    setErrorMsg('')
  }

  useEffect(() => {
    let mounted = true
    setOfficesLoading(true)
    userServices.getOffices()
      .then((list) => {
        if (!mounted) return
        const arr = Array.isArray(list) ? list : []
        setOffices(arr)
        if (arr.length === 1) setOfficeId(String(arr[0].id))
      })
      .catch(() => setOffices([]))
      .finally(() => { if (mounted) setOfficesLoading(false) })
    return () => { mounted = false }
  }, [])

  return (
    <div className="w-full md:max-w-3xl md:mx-auto p-4 sm:p-6">
      <Typography variant="h4" className="mb-1">Invitar visitas</Typography>
      <Typography variant="small" className="text-gray-600 mb-6">
        Envía invitaciones para que las personas completen sus datos y puedan ser autorizadas.
      </Typography>

      {isSuccess ? (
        <InviteSuccess
          sentInvites={sentInvites}
          onBack={backToForm}
          qrOpen={qrOpen}
          qrTarget={qrTarget}
          openQr={openQr}
          closeQr={closeQr}
        />
      ) : (
        <InviteForm
          mode={mode}
          setMode={setMode}
          date={date}
          setDate={setDate}
          startAt={startAt}
          setStartAt={setStartAt}
          endAt={endAt}
          setEndAt={setEndAt}
          officeId={officeId}
          setOfficeId={setOfficeId}
          offices={offices}
          officesLoading={officesLoading}
          invitees={invitees}
          setInvitees={setInvitees}
          handleSubmit={handleSubmit}
          errorMsg={errorMsg}
          isLoading={isLoading}
        />
      )}
    </div>
  )
}

export default VisitInviteForm